/**
 * BracketView - Tabbed bracket visualization.
 *
 * Tabs: East | West | South | Midwest | Final Four
 * Each tab shows one region at full width with connector lines,
 * or the Final Four / Championship center section.
 */

import { useState, useMemo } from 'react';
import BracketRegion from './BracketRegion';
import FinalFour from './FinalFour';

const REGIONS_ORDER = ['East', 'West', 'South', 'Midwest'];

/**
 * Determine which region a pick belongs to based on round_number and game_slot.
 * Slots are allocated sequentially per region:
 *   R1: 8 games/region → East 1-8, West 9-16, South 17-24, Midwest 25-32
 *   R2: 4 games/region → East 1-4, West 5-8, South 9-12, Midwest 13-16
 *   R3: 2 games/region, R4: 1 game/region
 *   R5+: Final Four (no region)
 */
function getPickRegion(pick, entriesMap) {
  if (pick.round_number >= 5) return null;

  // Try entry lookup first (most reliable when entries exist)
  const entry = pick.team1_entry_id ? entriesMap[pick.team1_entry_id]
    : pick.team2_entry_id ? entriesMap[pick.team2_entry_id]
    : pick.winner_entry_id ? entriesMap[pick.winner_entry_id]
    : null;
  if (entry?.region) return entry.region;

  // Fall back to slot-based calculation
  const gamesPerRegion = Math.pow(2, 4 - pick.round_number); // R1=8, R2=4, R3=2, R4=1
  const regionIdx = Math.floor((pick.game_slot - 1) / gamesPerRegion);
  return REGIONS_ORDER[regionIdx] || null;
}

const REGION_COLORS = {
  East: { bg: 'from-blue-500/15 to-blue-600/5', border: 'border-blue-500/40', text: 'text-blue-400', dot: 'bg-blue-400' },
  West: { bg: 'from-red-500/15 to-red-600/5', border: 'border-red-500/40', text: 'text-red-400', dot: 'bg-red-400' },
  South: { bg: 'from-emerald-500/15 to-emerald-600/5', border: 'border-emerald-500/40', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  Midwest: { bg: 'from-amber-500/15 to-amber-600/5', border: 'border-amber-500/40', text: 'text-amber-400', dot: 'bg-amber-400' },
};

function BracketView({
  bracket,
  entries,
  predictions = {},
  onPickWinner,
  onAnalyze,
  onAskKnewton,
  onMascotBattle,
  onPremiumGate,
  isPremium = false,
  readOnly = false,
}) {
  const [activeTab, setActiveTab] = useState('East');

  const entriesMap = useMemo(() => {
    const map = {};
    (entries || []).forEach(e => { map[e.id] = e; });
    return map;
  }, [entries]);

  const picks = bracket?.picks || [];
  const regions = REGIONS_ORDER.filter(r =>
    entries?.some(e => e.region === r)
  );

  // Count picks per region for progress indicators
  const pickCounts = {};
  regions.forEach(r => {
    pickCounts[r] = picks.filter(p =>
      getPickRegion(p, entriesMap) === r && p.round_number <= 4 && p.winner_entry_id
    ).length;
  });
  const f4Picks = picks.filter(p => p.round_number >= 5 && p.winner_entry_id).length;

  return (
    <div>
      {/* Tab bar */}
      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1 scrollbar-none">
        {regions.map(r => {
          const colors = REGION_COLORS[r];
          const isActive = activeTab === r;
          const progress = pickCounts[r] || 0;
          return (
            <button
              key={r}
              onClick={() => setActiveTab(r)}
              className={`
                relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 shrink-0
                ${isActive
                  ? `bg-gradient-to-r ${colors.bg} ${colors.text} border ${colors.border} shadow-lg`
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-card-hover border border-transparent'
                }
              `}
            >
              <span className={`w-2 h-2 rounded-full ${isActive ? colors.dot : 'bg-text-tertiary/40'}`} />
              {r}
              {progress > 0 && (
                <span className={`text-[10px] font-bold ${isActive ? 'opacity-70' : 'text-text-tertiary'}`}>
                  {progress}/15
                </span>
              )}
            </button>
          );
        })}

        {/* Final Four tab */}
        <button
          onClick={() => setActiveTab('final4')}
          className={`
            flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 shrink-0
            ${activeTab === 'final4'
              ? 'bg-gradient-to-r from-accent-purple/15 to-accent-blue/10 text-accent-purple border border-accent-purple/40 shadow-lg'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-card-hover border border-transparent'
            }
          `}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M5.166 2.621v.858c-1.035.148-2.059.33-3.071.543a.75.75 0 00-.584.859 6.753 6.753 0 006.138 5.6 6.73 6.73 0 002.743 1.346A6.707 6.707 0 019.279 15H8.54c-1.036 0-1.875.84-1.875 1.875V19.5h-.75a.75.75 0 000 1.5h12.17a.75.75 0 000-1.5h-.75v-2.625c0-1.036-.84-1.875-1.875-1.875h-.739a6.707 6.707 0 01-1.112-3.173 6.73 6.73 0 002.743-1.347 6.753 6.753 0 006.139-5.6.75.75 0 00-.585-.858 47.077 47.077 0 00-3.07-.543V2.62a.75.75 0 00-.658-.744 49.22 49.22 0 00-6.093-.377c-2.063 0-4.096.128-6.093.377a.75.75 0 00-.657.744z" />
          </svg>
          Final Four
          {f4Picks > 0 && (
            <span className={`text-[10px] font-bold ${activeTab === 'final4' ? 'opacity-70' : 'text-text-tertiary'}`}>
              {f4Picks}/3
            </span>
          )}
        </button>
      </div>

      {/* Active region */}
      {activeTab !== 'final4' && (
        <BracketRegion
          region={activeTab}
          picks={picks}
          entriesMap={entriesMap}
          predictions={predictions}
          onPickWinner={onPickWinner}
          onAnalyze={onAnalyze}
          onAskKnewton={onAskKnewton}
          onMascotBattle={onMascotBattle}
          onPremiumGate={onPremiumGate}
          isPremium={isPremium}
          readOnly={readOnly}
          regionColor={REGION_COLORS[activeTab]}
        />
      )}

      {/* Final Four */}
      {activeTab === 'final4' && (
        <FinalFour
          picks={picks}
          entriesMap={entriesMap}
          predictions={predictions}
          onPickWinner={onPickWinner}
          onAnalyze={onAnalyze}
          onAskKnewton={onAskKnewton}
          onMascotBattle={onMascotBattle}
          onPremiumGate={onPremiumGate}
          isPremium={isPremium}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

export default BracketView;
